import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';

import { QuestionAverageScoreDto } from '../analysis/models';
import { scoreColor } from '../analysis/score-color.util';
import { WorkEnvironmentAnalysisService } from '../analysis/services/work-environment-analysis.service';

/** A question's score with everything the row needs to render precomputed. */
interface QuestionRow {
  QuestionId: number;
  Number: number;
  Text: string;
  AverageScore: number;
  /** Fill width of the score bar, in percent of the 1-5 scale. */
  FillPercent: number;
  BarColor: string;
  /** Difference from the benchmark index, or null when there is no benchmark. */
  Delta: number | null;
}

/**
 * Average score of every question in one category, each on its own bar.
 *
 * Ported from the AMIL `StatisticQuestions` component. The prototype numbered
 * the rows with the raw question id, so the third category's list started at
 * "21."; the rows are numbered within the category here.
 */
@Component({
  selector: 'survey-question-scores',
  templateUrl: './survey-question-scores.component.html',
  styleUrls: ['./survey-question-scores.component.scss'],
  standalone: false
})
export class SurveyQuestionScoresComponent implements OnChanges, OnDestroy {
  @Input() surveyId: number | null = null;
  @Input() categoryId: number | null = null;

  /** Benchmark each question is compared against. Null hides the comparison. */
  @Input() benchmarkIndex: number | null = null;

  //#region Variables
  readonly maxScore = 5;

  rows: QuestionRow[] = [];
  isLoading = false;

  private subscription: Subscription | null = null;
  //#endregion

  constructor(private analysisService: WorkEnvironmentAnalysisService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('surveyId' in changes || 'categoryId' in changes) {
      this.load();
      return;
    }

    if ('benchmarkIndex' in changes) {
      this.rows = this.rows.map(row => ({
        ...row,
        Delta: this.deltaFor(row.AverageScore)
      }));
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  //#region Data
  private load(): void {
    if (!this.surveyId || !this.categoryId) {
      this.rows = [];
      return;
    }

    // A fast category switch must not let the previous response land last.
    this.subscription?.unsubscribe();

    this.isLoading = true;

    this.subscription = this.analysisService
      .getCategoryQuestionScores(this.surveyId, this.categoryId)
      .subscribe({
        next: result => {
          this.rows = result.Questions.map((question, index) =>
            this.toRow(question, index)
          );
          this.isLoading = false;
        },
        error: () => {
          this.rows = [];
          this.isLoading = false;
        }
      });
  }

  private toRow(question: QuestionAverageScoreDto, index: number): QuestionRow {
    return {
      QuestionId: question.QuestionId,
      Number: index + 1,
      Text: question.Text,
      AverageScore: question.AverageScore,
      FillPercent: (question.AverageScore / this.maxScore) * 100,
      BarColor: scoreColor(question.AverageScore, this.maxScore),
      Delta: this.deltaFor(question.AverageScore)
    };
  }

  private deltaFor(score: number): number | null {
    if (this.benchmarkIndex === null) {
      return null;
    }

    return Math.round((score - this.benchmarkIndex) * 10) / 10;
  }
  //#endregion
}
